import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { GridMenuIcon } from '../components/GridMenuIcon';
import { FolderSidebar } from '../components/BrainDump/FolderSidebar';
import { NoteCard } from '../components/BrainDump/NoteCard';
import { NoteEditor } from '../components/BrainDump/NoteEditor';
import { NoteViewer } from '../components/BrainDump/NoteViewer';
import { FolderManager } from '../components/BrainDump/FolderManager';
import { Plus, Search, Tag, Filter, Folder } from 'lucide-react';
import { useTaskContext } from '../contexts/TaskContext';
import { useNotes, type Note } from '../hooks/useNotes';
import type { Role } from '../types';

export function BrainDump() {
  const { notes, folders, addNote, updateNote, deleteNote, addFolder, updateFolder, deleteFolder } = useNotes();
  const { roles } = useTaskContext();
  const [showEditor, setShowEditor] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role>('all');
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [showMenu, setShowMenu] = useState(false);

  const allTags = Array.from(new Set(notes.flatMap(note => note.tags)));

  const filteredNotes = notes.filter(note => {
    const matchesSearch = 
      note.content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.text.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTags = selectedTags.length === 0 || (
      note.tags && selectedTags.every(tag => note.tags.includes(tag))
    );
    const matchesFolder = !selectedFolder || note.folderId === selectedFolder;
    const matchesRole = selectedRole === 'all' || note.role === selectedRole;
    return matchesSearch && matchesTags && matchesFolder && matchesRole;
  });

  const pinnedNotes = filteredNotes.filter(note => note.isPinned);
  const unpinnedNotes = filteredNotes.filter(note => !note.isPinned);

  const handleSave = async (noteData: { 
    content: Note['content']; 
    tags: string[]; 
    color: string;
    folderId?: string;
    role: Exclude<Role, 'all'>;
  }) => {
    if (editingNote) {
      await updateNote(editingNote.id, {
        ...noteData,
        isPinned: editingNote.isPinned,
      });
      setEditingNote(null);
    } else {
      await addNote({
        ...noteData,
        isPinned: false,
      });
    }
    setShowEditor(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this note?')) {
      await deleteNote(id);
    }
  };

  const handlePin = async (id: string) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      await updateNote(id, { isPinned: !note.isPinned });
    }
  };

  const handleEdit = (id: string) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      setEditingNote(note);
      setShowEditor(true);
    }
  };

  const handleView = (id: string) => {
    const note = notes.find(n => n.id === id);
    if (note) {
      setViewingNote(note);
      setShowViewer(true);
    }
  };

  const handleTagClick = (tag: string) => {
    // If tag is already selected, remove it
    if (selectedTags.includes(tag)) {
      setSelectedTags(prev => prev.filter(t => t !== tag));
    } else {
      // Add tag to selection
      setSelectedTags(prev => [...prev, tag]);
    }
  };

  return (
    <Layout>
      {/* Quick Add Brain Dump Button */}
      <button
        onClick={() => {
          setEditingNote(null);
          setShowEditor(true);
        }}
        className="fixed bottom-24 right-6 md:bottom-8 w-14 h-14 bg-[var(--accent-color)] rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity z-50"
      >
        <Plus className="w-6 h-6 text-black" />
      </button>

      <div className="flex flex-col md:flex-row gap-6">
        <FolderSidebar
          folders={folders}
          selectedFolder={selectedFolder}
          onFolderSelect={setSelectedFolder}
          onManageFolders={() => setShowFolderManager(true)}
        />
        <div className="flex-1 space-y-8 min-w-0">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl">Brain Dump</h1>
            </div>
            <div className="relative">
              <GridMenuIcon onClick={() => setShowMenu(!showMenu)} />
              
               {showMenu && (
                 <div className="absolute right-0 mt-2 w-56 bg-black rounded-xl shadow-lg py-2 z-50">
                   <div className="px-3 py-2">
                     <button
                       onClick={() => {
                         setEditingNote(null);
                         setShowEditor(true);
                         setShowMenu(false);
                       }}
                       className="flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-sm hover:bg-black/20 transition-colors"
                     >
                       <Plus className="w-4 h-4" />
                       Add Brain Dump
                     </button>
                     <button
                       onClick={() => {
                         setShowFolderManager(true);
                         setShowMenu(false);
                       }}
                       className="flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-sm hover:bg-black/20 transition-colors mt-1"
                     >
                       <Folder className="w-4 h-4" />
                       Manage Folders
                     </button>
                   </div>
                   
                   <div className="px-3 py-2 border-t border-gray-800">
                     {[{ id: 'all', name: 'All' }].concat(
                       roles.map(role => ({ id: role.id, name: role.name }))
                     ).map((role) => (
                       <button
                         key={role.id}
                         onClick={() => {
                           setSelectedRole(role.id as Role);
                           setShowMenu(false);
                         }}
                         className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                           selectedRole === role.id
                             ? 'bg-[#bbdde5] text-black'
                             : 'hover:bg-black/20'
                         }`}
                       >
                         {role.name}
                       </button>
                     ))}
                   </div>
                 </div>
               )}
             </div>
           </div>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-1/2 pl-10 pr-4 py-1.5 bg-black border-b border-white/50 text-sm text-white placeholder-white focus:border-white transition-colors bg-gradient-to-r from-black via-black to-transparent"
                />
              </div>
            </div>
            {allTags.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
                <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
                {allTags.map((tag, index) => (
                  <button
                    key={`${tag}-${index}`}
                    onClick={() => handleTagClick(tag)}
                    className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm transition-colors ${
                      selectedTags && selectedTags.includes(tag)
                        ? 'bg-[#bbdde5] text-black'
                        : 'bg-[#1A1B1E] text-white hover:bg-black/40'
                    }`}
                  >
                    <Tag className="w-4 h-4" />
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-8">
            {pinnedNotes.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl">Pinned Brain Dumps</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pinnedNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      {...note}
                      onPin={handlePin}
                      onEdit={handleEdit}
                      onView={handleView}
                      onDelete={handleDelete}
                      onTagClick={handleTagClick}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h2 className="text-xl">All Notes</h2>
              {unpinnedNotes.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  No notes yet. Start by adding one!
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {unpinnedNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      {...note}
                      onPin={handlePin}
                      onEdit={handleEdit}
                      onView={handleView}
                      onDelete={handleDelete}
                      onTagClick={handleTagClick}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showEditor && (
        <NoteEditor
          initialContent={editingNote?.content}
          initialTags={editingNote?.tags}
          initialColor={editingNote?.color}
          initialFolderId={editingNote?.folderId}
          folders={folders}
          onSave={handleSave}
          onClose={() => {
            setShowEditor(false);
            setEditingNote(null);
          }}
        />
      )}
      
      {showViewer && viewingNote && (
        <NoteViewer
          {...viewingNote}
          onClose={() => {
            setShowViewer(false);
            setViewingNote(null);
          }}
          onTagClick={handleTagClick}
        />
      )}
      
      {showFolderManager && (
        <FolderManager
          folders={folders}
          onAddFolder={addFolder}
          onUpdateFolder={updateFolder}
          onDeleteFolder={deleteFolder}
          onClose={() => setShowFolderManager(false)}
        />
      )}
    </Layout>
  );
}